import React, { useContext, useState } from "react";
import { useParams } from "react-router";
import { useQuery } from "@apollo/client";
import {
  GET_MATCH_BY_ID,
  MATCHES_WITH_PERMISSION,
} from "../../GraphQL/Matches.js";
import {
  GET_APPEARANCES_BY_MATCH_ID,
  APPEARANCES_WITH_PERMISSION,
} from "../../GraphQL/Appearances.js";
import { TEAMS_WITH_PERMISSION } from "../../GraphQL/Teams.js";
import { LoginContext } from "../../Components/Login/LoginContext";
import LoadingSpinner from "../../Components/UI/LoadingSpinner.js";
import PageError from "../../Components/UI/PageError.js";
import PageLayout from "../../Components/UI/PageLayout.js";
import AppearanceTable from "../../Components/Appearance/AppearanceTable.js";
import NewAppearanceButton from "../../Components/Appearance/NewAppearanceButton.js";
import QuickEntryTable from "../../Components/Appearance/QuickEntryTable.js";
import TeamButton from "../../Components/Team/TeamButton.js";
import SeasonButton from "../../Components/Season/SeasonButton.js";
import MatchOverview from "./MatchOverview.js";
import { GET_PLAYERS_BY_MATCH_ID } from "../../GraphQL/Players";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";

const MatchPage = () => {
  const { matchId } = useParams();
  const [homeQuickEntry, setHomeQuickEntry] = useState(false);
  const [awayQuickEntry, setAwayQuickEntry] = useState(false);

  const match = useQuery(GET_MATCH_BY_ID, {
    variables: { id: matchId },
  });

  const appearances = useQuery(GET_APPEARANCES_BY_MATCH_ID, {
    variables: { id: matchId },
  });

  const possiblePlayers = useQuery(GET_PLAYERS_BY_MATCH_ID, {
    variables: { id: matchId },
  });

  let matchesWithPermission = useQuery(MATCHES_WITH_PERMISSION, {});

  const appearancesWithPermission = useQuery(APPEARANCES_WITH_PERMISSION, {});

  let teamsWithPermission = useQuery(TEAMS_WITH_PERMISSION, {});

  const [auth] = useContext(LoginContext);

  const queryError =
    match.error ||
    appearances.error ||
    possiblePlayers.error ||
    matchesWithPermission.error ||
    appearancesWithPermission.error ||
    teamsWithPermission.error;
  if (queryError) {
    return (
      <PageError
        message={queryError.message}
        onRetry={() => {
          match.refetch();
          appearances.refetch();
          possiblePlayers.refetch();
          matchesWithPermission.refetch();
          appearancesWithPermission.refetch();
          teamsWithPermission.refetch();
        }}
      />
    );
  }

  if (
    match.loading ||
    appearances.loading ||
    possiblePlayers.loading ||
    matchesWithPermission.loading ||
    appearancesWithPermission.loading ||
    teamsWithPermission.loading
  )
    return <LoadingSpinner />;

  const homePlayers = possiblePlayers.data.playersByMatchId.filter((player) => {
    return player.team.id === match.data.match.homeTeam.id;
  });

  const awayPlayers = possiblePlayers.data.playersByMatchId.filter((player) => {
    return player.team.id === match.data.match.awayTeam.id;
  });

  const homeAppearances = appearances.data.appearancesByMatchId.filter(
    (appearance) => {
      return appearance.team.id === match.data.match.homeTeam.id;
    }
  );

  const awayAppearances = appearances.data.appearancesByMatchId.filter(
    (appearance) => {
      return appearance.team.id === match.data.match.awayTeam.id;
    }
  );

  const homeGoals = homeAppearances.reduce((accumulator, appearance) => {
    return accumulator + appearance.goals;
  }, 0);

  const awayGoals = awayAppearances.reduce((accumulator, appearance) => {
    return accumulator + appearance.goals;
  }, 0);

  matchesWithPermission = matchesWithPermission.data.matchesWithPermission.map(
    (match) => match.id
  );

  teamsWithPermission = teamsWithPermission.data.teamsWithPermission.map(
    (team) => team.id
  );

  const canCreateAppearanceHome =
    auth.loggedIn &&
    Array.isArray(teamsWithPermission) &&
    teamsWithPermission.includes(parseInt(match.data.match.homeTeam.id));

  const canCreateAppearanceAway =
    auth.loggedIn &&
    Array.isArray(teamsWithPermission) &&
    teamsWithPermission.includes(parseInt(match.data.match.awayTeam.id));

  return (
    <PageLayout
      title={`${match.data.match.homeTeam.name} vs ${match.data.match.awayTeam.name}`}
      breadcrumbs={[
        { label: "Home", to: "/" },
        {
          label: match.data.match.season.competition.name,
          to: "/competition/" + match.data.match.season.competition.id,
        },
        {
          label: match.data.match.season.name,
          to: "/season/" + match.data.match.season.id,
        },
        { label: "Match" },
      ]}
    >
      <MatchOverview
        match={match.data.match}
        homeGoals={homeGoals}
        awayGoals={awayGoals}
      />

      <TeamButton team={match.data.match.homeTeam} />
      <TeamButton team={match.data.match.awayTeam} />
      <SeasonButton season={match.data.match.season} />

      <Grid container spacing={3} sx={{ mt: 3 }}>
        <Grid item xs={12} md={6}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
            <Typography variant="h5">
              {match.data.match.homeTeam.name} Appearances
            </Typography>
            {canCreateAppearanceHome && (
              <Button
                size="small"
                variant={homeQuickEntry ? "outlined" : "text"}
                onClick={() => setHomeQuickEntry((v) => !v)}
              >
                {homeQuickEntry ? "Close Quick Entry" : "Quick Entry"}
              </Button>
            )}
          </Box>
          <AppearanceTable
            appearancesWithPermission={
              appearancesWithPermission.data.appearancesWithPermission
            }
            appearances={homeAppearances}
          />
          {canCreateAppearanceHome && !homeQuickEntry && (
            <NewAppearanceButton
              matchId={matchId}
              players={homePlayers}
              teamId={match.data.match.homeTeam.id}
            />
          )}
          {homeQuickEntry && (
            <QuickEntryTable
              players={homePlayers}
              matchId={matchId}
              teamId={match.data.match.homeTeam.id}
              seasonId={match.data.match.season.id}
              onDone={() => setHomeQuickEntry(false)}
            />
          )}
        </Grid>

        <Grid item xs={12} md={6}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
            <Typography variant="h5">
              {match.data.match.awayTeam.name} Appearances
            </Typography>
            {canCreateAppearanceAway && (
              <Button
                size="small"
                variant={awayQuickEntry ? "outlined" : "text"}
                onClick={() => setAwayQuickEntry((v) => !v)}
              >
                {awayQuickEntry ? "Close Quick Entry" : "Quick Entry"}
              </Button>
            )}
          </Box>
          <AppearanceTable
            appearancesWithPermission={
              appearancesWithPermission.data.appearancesWithPermission
            }
            appearances={awayAppearances}
          />
          {canCreateAppearanceAway && !awayQuickEntry && (
            <NewAppearanceButton
              matchId={matchId}
              teamId={match.data.match.awayTeam.id}
              players={awayPlayers}
            />
          )}
          {awayQuickEntry && (
            <QuickEntryTable
              players={awayPlayers}
              matchId={matchId}
              teamId={match.data.match.awayTeam.id}
              seasonId={match.data.match.season.id}
              onDone={() => setAwayQuickEntry(false)}
            />
          )}
        </Grid>
      </Grid>
    </PageLayout>
  );
};

export default MatchPage;
